import {API, Auth, Cache} from "aws-amplify"
import {navigate} from "gatsby"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {CognitoRefreshToken} from "amazon-cognito-identity-js"

import {getLanguagePathFromStorage, useTranslation} from "libs/gatsby-i18next"
import {AppCenterApiName} from "../constants"
import {QUERY_KEY_GIANT_USER} from "services/appCenter/hooks"
import {useConfirmModal} from "stores/modals"
import {
  resetCache,
  setLoginInfo,
  setLoginInfoIfNotExisted,
  hasCallbackClient,
} from "./cache"
import {resetUserStore} from "stores/user"
import {isEmail} from "utils/string"
import {
  isReachSendingMessageLimit,
  isSuspiciousPhoneNumber,
} from "utils/user-lambda-validation-error"
import {useEffect} from "react"
import {
  resetSocialLoginLoadingAtom,
  showAutoCloseLoadingAtom,
  socialLoginLoadingAtom,
} from "components/feature/sign-in/atoms"
import {useAtomValue, useSetAtom} from "jotai"
import {AvailableSocialProvider} from "./schemas"
import {federatedSignIn} from "./federated-sign-in"

/** @typedef {{ [key: string]: string }} ClientMetadata */

/**
 * @typedef {{gid: string, registerFrom: import("services/appCenter/data").TGiantRegisterFrom}} TVerifyUsernameResponse
 * @param {string} username
 * @returns {Promise<TVerifyUsernameResponse>}
 */
export function cognitoVerifyUsername(username) {
  const queryKey = isEmail(username) ? "email" : "phoneNumber"
  return API.get(
    AppCenterApiName.AppCenterApi,
    `/users?${queryKey}=${encodeURIComponent(username)}`,
    {},
  )
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * TVerifyUsernameResponse,
 * any,
 * string,
 * undefined
 * >} [options]
 * @returns
 */
export const useCognitoVerifyUsername = (options = {}) => {
  const mutation = useMutation({
    mutationKey: ["cognitoVerifyUsername"],
    mutationFn: cognitoVerifyUsername,
    retry: 0,
    ...options,
  })
  return {
    verify: mutation.mutate,
    verifyAsync: mutation.mutateAsync,
    ...mutation,
  }
}
/**
 * @typedef {{
 *  code: string
 *  name: string
 *  message: string
 * }} TCognitoSignInError
 */

/**
 * @typedef {{username: string, password: string}} TPasswordSignInParams
 */
/**
 * @param {TPasswordSignInParams} param0
 * @returns {Promise<CognitoUser>}
 */
export function passwordSignIn({username, password}) {
  return Auth.signIn(username, password)
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * CognitoUser,
 * TCognitoSignInError,
 * TPasswordSignInParams,
 * undefined
 * >} [options]
 * @returns
 */
export const useCognitoSignIn = (options = {}) => {
  const {onSuccess, ...restOptions} = options
  return useMutation({
    mutationKey: ["cognitoSignIn"],
    mutationFn: passwordSignIn,
    retry: 0,
    onSuccess: (...args) => {
      const cognitoUser = args[0]
      if (cognitoUser.challengeName !== "SMS_MFA") {
        setLoginInfo({
          way: "General",
          sub: cognitoUser.attributes.sub,
          username: cognitoUser.getUsername(),
        })
      }
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * {
 *  provider: import("./types").TAvailableSocialProvider,
 *  customState: import("services/auth/types").FederatedSignInCustomState
 * },
 * undefined
 * >} [options]
 * @returns
 */
export const useCognitoSocialLogin = (options = {}) => {
  const socialLoginLoading = useAtomValue(socialLoginLoadingAtom)
  const resetSocialLoginLoading = useSetAtom(resetSocialLoginLoadingAtom)
  const showAutoCloseLoading = useSetAtom(showAutoCloseLoadingAtom)
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  const {mutate} = useMutation({
    mutationKey: ["cognitoSocialLogin"],
    mutationFn: ({provider, customState}) => {
      if (provider === AvailableSocialProvider.enum.LINE) {
        Cache.setItem("lineLogin", true)
      }

      return federatedSignIn({
        provider,
        customState,
      })
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })

      /**
       * Show a temporary loading animation for up to 5 seconds
       * to prevent users from clicking the social button again
       */
      showAutoCloseLoading({closeAfterMS: 5000})

      onSuccess?.(...args)
    },
    ...restOptions,
  })

  useEffect(() => {
    /**
     * @param {{ persisted: boolean; }} event
     */
    function persistHandler(event) {
      if (event.persisted) {
        resetSocialLoginLoading() // reset stale loading state from bfcache
      }
    }

    window.addEventListener("pageshow", persistHandler)

    return () => {
      window.removeEventListener("pageshow", persistHandler)
      resetSocialLoginLoading()
    }
  }, [])

  return {
    socialLogin: mutate,
    isLoading: socialLoginLoading,
  }
}

/** @returns {Promise<CognitoUser>} */
export const currentAuthenticatedUser = () => Auth.currentAuthenticatedUser()

/**
 * @param {CognitoUser} user
 * @returns {Promise<CognitoUser>}
 */
const refreshSession = async user => {
  let token = user.getSignInUserSession()?.getRefreshToken()?.getToken()
  if (token) {
    const refreshToken = new CognitoRefreshToken({RefreshToken: token})
    return new Promise((resolve, reject) => {
      user.refreshSession(refreshToken, (err, _session) => {
        if (err) {
          return reject(err)
        } else {
          return resolve(user)
        }
      })
    })
  }
  return user
}

/**
 * allow caller to skip the navigate to sign-in in when error
 * (for example, the sign in page don't want to navigate to self again)
 * @param {{skipErrorNavigate?: boolean} & Pick<import("@tanstack/react-query").UseQueryOptions<
 *   CognitoUser,
 *   any,
 *   CognitoUser,
 *   string[]
 * >,  "onSuccess" | "refetchOnMount" | "refetchOnReconnect" | "refetchOnWindowFocus" | "enabled">} options
 */
export const useCognitoUser = (options = {}) => {
  const {onSuccess, skipErrorNavigate = false, ...restOptions} = options
  return useQuery({
    queryKey: ["cognitoUser"],
    queryFn: () =>
      currentAuthenticatedUser().then(user =>
        // if client-id is set,
        // force refresh session in case of token revoked by this client
        hasCallbackClient() ? refreshSession(user) : user,
      ),
    onError: () => {
      if (skipErrorNavigate) return
      resetCache()
      resetUserStore()

      // check `getLanguagePathFromStorage` to see why
      // here we don't use navigate from useI18next
      navigate(getLanguagePathFromStorage("/auth/sign-in"))
    },
    onSuccess: (...args) => {
      setLoginInfoIfNotExisted(args[0])
      onSuccess?.(...args)
    },
    retry: 0,
    ...restOptions,
  })
}

/**
 * @typedef {import('@aws-amplify/auth').SignUpParams} ISignUpParams
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * import('amazon-cognito-identity-js').ISignUpResult,
 * any,
 * ISignUpParams,
 * undefined
 * >} [options]
 * @returns
 */
export const useCognitoSignUp = options => {
  const {mutate, ...restAttrs} = useMutation({
    mutationKey: ["cognitoSignUp"],
    mutationFn: ({
      username,
      password,
      attributes,
      clientMetadata,
      validationData,
    }) =>
      Auth.signUp({
        username,
        password,
        attributes,
        clientMetadata,
        validationData,
      }),
    ...options,
  })

  return {
    signUp: mutate,
    ...restAttrs,
  }
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * string,
 * undefined
 * >} [options]
 * @returns
 */
export const useCognitoResendVerificationCode = options => {
  const {mutate, ...restAttrs} = useMutation({
    mutationKey: ["cognitoResendVerificationCode"],
    mutationFn: username => Auth.resendSignUp(username),
    ...options,
  })

  return {
    resend: mutate,
    ...restAttrs,
  }
}

/**
 * @typedef {{
 *  username: string
 *  code: string
 * }} ConfirmSignUpParams
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * ConfirmSignUpParams,
 * undefined
 * >} [options]
 * @returns
 */
export const useCognitoConfirmSignUp = options => {
  const {mutate, ...restAttrs} = useMutation({
    mutationKey: ["cognitoConfirmSignUp"],
    mutationFn: ({username, code}) => Auth.confirmSignUp(username, code),
    ...options,
  })

  return {
    confirm: mutate,
    ...restAttrs,
  }
}

/**
 * @typedef {{
 *  oldPassword: string
 *  newPassword: string
 * }} ChangePasswordParams
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * ChangePasswordParams,
 * undefined
 * >} [options]
 * @returns
 */
export const useCognitoChangePassword = options => {
  const cognitoUser = useCognitoUser()
  return useMutation({
    mutationKey: ["cognitoChangePassword"],
    mutationFn: ({oldPassword, newPassword}) =>
      Auth.changePassword(cognitoUser.data, oldPassword, newPassword),
    ...options,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * {
 *  CodeDeliveryDetails: {
 *   AttributeName: 'email' | 'phone_number'
 *   DeliveryMedium: 'EMAIL' | 'SMS'
 *   Destination: string
 * }},
 * any,
 * string,
 * undefined
 * >} [options]
 * @returns
 */
export function useForgotPassword(options = {}) {
  return useMutation({
    mutationKey: ["forgotPassword"],
    mutationFn: username => Auth.forgotPassword(username),
    ...options,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * {username: string, code: string, newPassword: string, clientMetadata?: ClientMetadata},
 * undefined
 * >} [options]
 * @returns
 */
export function useForgotPasswordSubmit(options = {}) {
  return useMutation({
    mutationKey: ["forgotPasswordSubmit"],
    mutationFn: ({username, code, newPassword, clientMetadata}) =>
      Auth.forgotPasswordSubmit(username, code, newPassword, clientMetadata),
    ...options,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * "phone_number" | "email" | undefined,
 * undefined
 * >} [options]
 */
export function useVerifyUserAttribute(options = {}) {
  const {onError, ...restOptions} = options
  const {t} = useTranslation()
  const {createConfirmModal} = useConfirmModal()

  return useMutation({
    mutationKey: ["resendPhoneVerificationCode"],
    mutationFn: async (attr = "phone_number") => {
      const user = await Auth.currentAuthenticatedUser()
      return Auth.verifyUserAttribute(user, attr)
    },
    onError: (...args) => {
      const error = args[0]
      switch (error.code) {
        case "LimitExceededException":
          createConfirmModal(t("common.verificationResendLimitExceed"))
          break
        case "UserLambdaValidationException":
          if (isReachSendingMessageLimit(error.message)) {
            createConfirmModal(t("common.verificationResendLimitExceed"))
          }
          break
        default:
          break
      }
      onError?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @typedef {{
 *   attr: any,
 *   field?: 'phone_number' | 'email'
 *   clientMetadata?: ClientMetadata
 * }} UpdateUserAttributesWithVerificationParam
 */

/**
 * @param {UpdateUserAttributesWithVerificationParam} param
 */
async function updateUserAttributesWithVerification({
  attr,
  field = "email",
  clientMetadata,
}) {
  const user = await Auth.currentAuthenticatedUser()

  // get user's attribute from cognito
  const origUserAttributes = await Auth.userAttributes(user)

  const origFieldData = origUserAttributes
    .find(attribute => attribute.getName() === field)
    ?.getValue()
  const newFieldData = Reflect.get(attr, field)

  // just resend the verification when these two are the same
  if (origFieldData === newFieldData) {
    await Auth.verifyUserAttribute(user, field, clientMetadata)
    return {
      method: "verify",
    }
  } else {
    await Auth.updateUserAttributes(user, attr, clientMetadata)
    return {
      method: "update",
    }
  }
}

/**
 * @typedef {import("@tanstack/react-query").UseMutationOptions<
 * Awaited<ReturnType<UpdateUserAttributesWithVerificationParam>>,
 * any,
 * UpdateUserAttributesWithVerificationParam,
 * undefined
 * > & {
 *  suppressDefaultErrorModal?: boolean
 * }} TUpdateUserAttributesWithVerificationOptions
 */

/**
 * @param {TUpdateUserAttributesWithVerificationOptions} [options]
 */
export function useUpdateUserAttributesWithVerification(options = {}) {
  const {onError, suppressDefaultErrorModal = false, ...restOptions} = options
  const {t} = useTranslation()
  const {createConfirmModal} = useConfirmModal()

  return useMutation({
    mutationKey: ["updateUserAttributesWithVerification"],
    mutationFn: updateUserAttributesWithVerification,
    retry: 0,
    onError: (...args) => {
      const error = args[0]
      if (suppressDefaultErrorModal) {
        onError?.(...args)
        return
      }
      switch (error.code) {
        case "LimitExceededException":
          createConfirmModal(t("common.verificationResendLimitExceed"))
          break
        case "UserLambdaValidationException":
          if (isReachSendingMessageLimit(error.message)) {
            createConfirmModal(t("common.verificationResendLimitExceed"))
          } else if (isSuspiciousPhoneNumber(error.message)) {
            createConfirmModal(
              t("page.signup.fieldErrorMessage.suspiciousNumber"),
            )
          } else {
            createConfirmModal(t("common.generalErrorMessage"))
          }
          break
        default:
          createConfirmModal(t("common.generalErrorMessage"))
      }
      onError?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * CognitoUser,
 * any,
 * {user: CognitoUser, code: string},
 * undefined
 * >} [options]
 * @returns
 */
export function useCongnitoConfirmSignin(options = {}) {
  const {onSuccess, ...restOptions} = options
  return useMutation({
    mutationKey: ["cognitoConfirmSignin"],
    mutationFn: ({user, code}) => Auth.confirmSignIn(user, code, "SMS_MFA"),
    onSuccess: (...args) => {
      const cognitoUser = args[0]
      setLoginInfo({
        way: "General",
        sub: cognitoUser.getSignInUserSession()?.getIdToken().payload["sub"],
        username: cognitoUser.getUsername(),
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * Awaited<ReturnType<typeof Auth.userAttributes>>,
 * any,
 * void,
 * undefined
 * >} [options]
 */
export function useFetchUserAttributes(options) {
  return useMutation({
    mutationKey: ["updateUserAttributes"],
    mutationFn: async () => {
      const user = await Auth.currentAuthenticatedUser()
      return Auth.userAttributes(user)
    },
    retry: 2,
    ...options,
  })
}

/**
 * @typedef {{
 *   attributes: {
 *     family_name?: string,
 *     given_name?: string,
 *     sub: string,
 *     identities?: string
 *     email?: string,
 *   }
 * } & import('amazon-cognito-identity-js').CognitoUser} CognitoUser
 */

/**
 * @param {Parameters<Auth['signOut']>[0]} options
 */
export const cognitoSignOut = async (options = undefined) => {
  resetCache()
  resetUserStore()
  await Auth.signOut(options)
}
